import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Flame, Loader2, Ruler, Search, Timer } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Slider } from '../ui/slider';
import RunningExerciseCard from '../training/RunningExerciseCard';
import RunningExerciseDetail from '../training/RunningExerciseDetail';
import { sumSessionDistanceKmFromSplits } from './plannerUtils';

function DraggableLibraryCard({ session, onOpenSessionDetail }) {
  const dragId =
    session.id != null && session.id !== ''
      ? `library-${session.id}`
      : `library-ex-${session.exercise_id}`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
    data: {
      type: 'library',
      trainingSessionId: session.id != null ? session.id : null,
      trainingExerciseId: session.exercise_id,
      needsPlannerSession: !!session.needs_planner_session,
      title: session.name,
      description: session.description,
      preview_splits: session.preview_splits,
    },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px,${transform.y}px,0)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('touch-none', isDragging && 'opacity-50')}
      {...listeners}
      {...attributes}
    >
      <RunningExerciseCard
        title={session.name}
        description={session.description}
        splits={session.preview_splits}
        vizHeightPx={40}
        className="cursor-grab active:cursor-grabbing"
        onOpenDetail={
          typeof onOpenSessionDetail === 'function'
            ? () => onOpenSessionDetail(session)
            : undefined
        }
      />
    </div>
  );
}

const DURATION_MIN = 5;
const DURATION_MAX = 480;
const LENGTH_MIN = 0;
const LENGTH_MAX = 80;
const INTENSITY_MIN = 0;
const INTENSITY_MAX = 100;
const PAGE_SIZE = 60;

function sessionStableKey(scope, s) {
  return `${scope}-${s.id ?? 'ex'}-${s.exercise_id}`;
}

export default function PlannerLibraryPanel({ scope, onScopeChange }) {
  const { t } = useLanguage();
  const [sessionDetailOpen, setSessionDetailOpen] = useState(false);
  const [sessionDetailId, setSessionDetailId] = useState(null);
  const [sessionDetailExercise, setSessionDetailExercise] = useState(null);
  const [sessionDetailTitle, setSessionDetailTitle] = useState('');
  const [subtypes, setSubtypes] = useState([]);
  const [subtypeFilter, setSubtypeFilter] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextOffset, setNextOffset] = useState(0);
  const loadMoreLockRef = useRef(false);
  const listFetchGenRef = useRef(0);
  const listScrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const [filterAxis, setFilterAxis] = useState('duration');
  const [durationRange, setDurationRange] = useState([DURATION_MIN, DURATION_MAX]);
  const [lengthRangeKm, setLengthRangeKm] = useState([LENGTH_MIN, LENGTH_MAX]);
  const [intensityRangePct, setIntensityRangePct] = useState([INTENSITY_MIN, INTENSITY_MAX]);

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(tmr);
  }, [q]);

  useEffect(() => {
    api
      .get('/api/training/running-workout-subtypes')
      .then((res) => setSubtypes(res.data.subtypes || []))
      .catch(() => setSubtypes([]));
  }, []);

  const fetchPage = useCallback(
    async (offset, { append, gen }) => {
      const params = { limit: PAGE_SIZE, offset };
      if (debouncedQ) params.q = debouncedQ;
      if (subtypeFilter) params.subtype_key = subtypeFilter;
      const url =
        scope === 'global' ? '/api/training/running-sessions' : '/api/training/planner-running-sessions';
      const res = await api.get(url, { params });
      if (gen != null && gen !== listFetchGenRef.current) return;
      const rows = res.data.sessions || [];
      const more =
        scope === 'global' ? rows.length === PAGE_SIZE : Boolean(res.data.hasMore);
      if (append) {
        setSessions((prev) => {
          const seen = new Set(prev.map((s) => sessionStableKey(scope, s)));
          const merged = [...prev];
          for (const s of rows) {
            const k = sessionStableKey(scope, s);
            if (!seen.has(k)) {
              seen.add(k);
              merged.push(s);
            }
          }
          return merged;
        });
      } else {
        setSessions(rows);
      }
      setNextOffset(offset + rows.length);
      setHasMore(more);
    },
    [scope, debouncedQ, subtypeFilter]
  );

  useEffect(() => {
    let cancelled = false;
    listFetchGenRef.current += 1;
    const gen = listFetchGenRef.current;
    setLoading(true);
    setLoadingMore(false);
    setHasMore(true);
    setNextOffset(0);
    loadMoreLockRef.current = false;
    fetchPage(0, { append: false, gen })
      .catch((e) => {
        console.error(e);
        if (!cancelled && gen === listFetchGenRef.current) {
          setSessions([]);
          setHasMore(false);
        }
      })
      .finally(() => {
        if (!cancelled && gen === listFetchGenRef.current) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadMoreLockRef.current || loading || loadingMore || !hasMore) return;
    const gen = listFetchGenRef.current;
    loadMoreLockRef.current = true;
    setLoadingMore(true);
    try {
      await fetchPage(nextOffset, { append: true, gen });
    } catch (e) {
      console.error(e);
      if (gen === listFetchGenRef.current) setHasMore(false);
    } finally {
      setLoadingMore(false);
      loadMoreLockRef.current = false;
    }
  }, [fetchPage, nextOffset, hasMore, loading, loadingMore]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    const root = listScrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreRef.current();
        }
      },
      { root, rootMargin: '100px', threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [scope, debouncedQ, subtypeFilter, sessions.length]);

  useEffect(() => {
    setDurationRange([DURATION_MIN, DURATION_MAX]);
    setLengthRangeKm([LENGTH_MIN, LENGTH_MAX]);
    setIntensityRangePct([INTENSITY_MIN, INTENSITY_MAX]);
  }, [scope]);

  const filtered = useMemo(() => {
    const [d0, d1] = durationRange;
    const [l0, l1] = lengthRangeKm;
    const [i0, i1] = intensityRangePct;
    return sessions.filter((s) => {
      const durSec = s.estimated_duration_sec;
      if (durSec != null && Number.isFinite(Number(durSec))) {
        const dm = Number(durSec) / 60;
        if (dm < d0 || dm > d1) return false;
      }
      const km = sumSessionDistanceKmFromSplits(s.preview_splits);
      if (km != null) {
        if (km < l0 || km > l1) return false;
      }
      const wi = s.weighted_intensity;
      if (wi != null && Number.isFinite(Number(wi))) {
        const p = Number(wi) * 100;
        if (p < i0 || p > i1) return false;
      }
      return true;
    });
  }, [sessions, durationRange, lengthRangeKm, intensityRangePct]);

  const filterLabel =
    filterAxis === 'duration'
      ? t('trainingLog.plannerFilterDuration', {
          min: String(durationRange[0]),
          max: String(durationRange[1]),
        })
      : filterAxis === 'length'
        ? t('trainingLog.plannerFilterLength', {
            min: String(lengthRangeKm[0]),
            max: String(lengthRangeKm[1]),
          })
        : t('trainingLog.plannerFilterIntensity', {
            min: String(intensityRangePct[0]),
            max: String(intensityRangePct[1]),
          });

  return (
    <div className="flex h-full min-h-0 w-[20%] min-w-[12rem] max-w-[22rem] flex-col self-stretch border-r border-border bg-muted/20">
      <div className="shrink-0 space-y-2 border-b border-border p-2">
        <p className="text-xs font-semibold text-foreground">{t('trainingLog.plannerLibraryTitle')}</p>
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant={scope === 'global' ? 'secondary' : 'ghost'}
            className="h-7 flex-1 text-[10px]"
            onClick={() => onScopeChange('global')}
          >
            {t('trainingLog.plannerScopeGlobal')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={scope === 'user' ? 'secondary' : 'ghost'}
            className="h-7 flex-1 text-[10px]"
            onClick={() => onScopeChange('user')}
          >
            {t('trainingLog.plannerScopeMine')}
          </Button>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('trainingLog.plannerSearchPlaceholder')}
            className="h-8 pl-7 text-xs"
            aria-label={t('trainingLog.plannerSearchPlaceholder')}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground">{t('trainingLog.plannerSubtype')}</Label>
          <Select value={subtypeFilter || '__all'} onValueChange={(v) => setSubtypeFilter(v === '__all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder={t('trainingLog.plannerSubtypeAll')} />
            </SelectTrigger>
            <SelectContent className="z-[10280]">
              <SelectItem value="__all" className="text-xs">
                {t('trainingLog.plannerSubtypeAll')}
              </SelectItem>
              {subtypes.map((st) => (
                <SelectItem key={st.id} value={st.subtype_key} className="text-xs">
                  {st.label_key ? t(st.label_key) : st.subtype_key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-md border border-border/80 bg-card/40 p-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            <Label className="text-[10px] leading-tight text-muted-foreground">{filterLabel}</Label>
            <div className="flex shrink-0 gap-0.5">
              <Button
                type="button"
                variant={filterAxis === 'duration' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                aria-label={t('trainingLog.plannerFilterDurationAxis')}
                aria-pressed={filterAxis === 'duration'}
                onClick={() => setFilterAxis('duration')}
              >
                <Timer className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant={filterAxis === 'length' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                aria-label={t('trainingLog.plannerFilterLengthAxis')}
                aria-pressed={filterAxis === 'length'}
                onClick={() => setFilterAxis('length')}
              >
                <Ruler className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant={filterAxis === 'intensity' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-7 w-7"
                aria-label={t('trainingLog.plannerFilterIntensityAxis')}
                aria-pressed={filterAxis === 'intensity'}
                onClick={() => setFilterAxis('intensity')}
              >
                <Flame className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          {filterAxis === 'duration' && (
            <Slider
              value={durationRange}
              min={DURATION_MIN}
              max={DURATION_MAX}
              step={5}
              minStepsBetweenThumbs={1}
              onValueChange={(v) => setDurationRange(v)}
              className="py-1"
            />
          )}
          {filterAxis === 'length' && (
            <Slider
              value={lengthRangeKm}
              min={LENGTH_MIN}
              max={LENGTH_MAX}
              step={0.5}
              minStepsBetweenThumbs={0.5}
              onValueChange={(v) => setLengthRangeKm(v)}
              className="py-1"
            />
          )}
          {filterAxis === 'intensity' && (
            <Slider
              value={intensityRangePct}
              min={INTENSITY_MIN}
              max={INTENSITY_MAX}
              step={5}
              minStepsBetweenThumbs={1}
              onValueChange={(v) => setIntensityRangePct(v)}
              className="py-1"
            />
          )}
        </div>
      </div>
      <div
        ref={listScrollRef}
        className="citius-scrollbar min-h-0 flex-1 space-y-2 overflow-y-auto p-2"
      >
        {loading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-center text-[10px] text-muted-foreground">{t('trainingLog.plannerLibraryEmpty')}</p>
        )}
        {!loading &&
          filtered.map((s) => (
            <DraggableLibraryCard
              key={sessionStableKey(scope, s)}
              session={s}
              onOpenSessionDetail={(sess) => {
                if (sess.id != null) {
                  setSessionDetailId(sess.id);
                  setSessionDetailExercise(null);
                  setSessionDetailTitle(sess.name || '');
                  setSessionDetailOpen(true);
                } else if (sess.exercise_id) {
                  setSessionDetailId(null);
                  api
                    .get(`/api/training/exercises/${sess.exercise_id}`)
                    .then((res) => {
                      setSessionDetailExercise(res.data?.exercise || null);
                      setSessionDetailTitle(res.data?.exercise?.name || sess.name || '');
                      setSessionDetailOpen(true);
                    })
                    .catch((e) => {
                      console.error(e);
                    });
                }
              }}
            />
          ))}
        {!loading && hasMore && (
          <>
            <div ref={sentinelRef} className="h-px w-full shrink-0" aria-hidden />
            {loadingMore && (
              <div className="flex justify-center py-3">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </>
        )}
      </div>

      <RunningExerciseDetail
        open={sessionDetailOpen}
        onOpenChange={(o) => {
          setSessionDetailOpen(o);
          if (!o) {
            setSessionDetailId(null);
            setSessionDetailExercise(null);
            setSessionDetailTitle('');
          }
        }}
        mode={sessionDetailId ? 'library' : 'trainingExercise'}
        librarySessionId={sessionDetailId}
        trainingExercise={sessionDetailExercise}
        fallbackTitle={sessionDetailTitle}
      />
    </div>
  );
}
